import {
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  Collapse,
  Divider,
  Stack,
} from "@mui/material";
import { useState } from "react";
import {
  Home as HomeIcon,
  Refresh as RefreshIcon,
  BugReport as BugReportIcon,
} from "@mui/icons-material";
import {
  getUserFriendlyMessage,
  getErrorSeverity,
  copyErrorToClipboard,
} from "./ErrorUtils";

export default function ErrorDisplay({
  error,
  errorInfo = null,
  onGoHome,
  onRefresh,
}) {
  const [showDetails, setShowDetails] = useState(false);

  const userMessage = getUserFriendlyMessage(error);
  const severity = getErrorSeverity(error);

  const getErrorTitle = () => {
    if (error.status === 404) {
      return "Page Not Found";
    }

    if (error.status === 500) {
      return "Internal Server Error";
    }

    if (error.status >= 400 && error.status < 500) {
      return `Client Error (${error.status})`;
    }

    if (error.status >= 500) {
      return `Server Error (${error.status})`;
    }

    return "Application Error";
  };

  const handleReportBug = async () => {
    const errorDetails = {
      ...error,
      ...(errorInfo && { componentStack: errorInfo.componentStack }),
    };

    const success = await copyErrorToClipboard(errorDetails);
    if (success) {
      alert(
        "Error details copied to clipboard. Please send this to Sam on Teams"
      );
    } else {
      alert("Please contact Sam on Teams with the error details");
    }
  };

  const getErrorDetails = () => {
    const details = {
      message: error.message,
      name: error.name,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };

    // Add status-specific fields for router errors
    if (error.status) {
      details.status = error.status;
      details.statusText = error.statusText;
    }

    return details;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        bgcolor: "background.default",
      }}>
      <Paper
        elevation={3}
        sx={{
          maxWidth: 600,
          width: "100%",
          p: 4,
          textAlign: "center",
        }}>
        <Typography
          variant='h3'
          color='error'
          gutterBottom
          sx={{ fontWeight: "bold" }}>
          {getErrorTitle()}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant='h6' sx={{ mb: 2 }}>
          {userMessage}
        </Typography>

        <Alert severity={severity} sx={{ mb: 3, textAlign: "left" }}>
          <Typography variant='body2'>
            <strong>Error:</strong> {error.message || "Unknown error occurred"}
          </Typography>
          {error.status && (
            <Typography variant='body2'>
              <strong>Error Code:</strong> {error.status}
            </Typography>
          )}
          {error.statusText && (
            <Typography variant='body2'>
              <strong>Status:</strong> {error.statusText}
            </Typography>
          )}
        </Alert>

        <Stack
          direction='row'
          spacing={2}
          justifyContent='center'
          sx={{ mb: 3 }}>
          <Button
            variant='contained'
            startIcon={<HomeIcon />}
            onClick={onGoHome}>
            Go Home
          </Button>
          <Button
            variant='outlined'
            startIcon={<RefreshIcon />}
            onClick={onRefresh}>
            Refresh Page
          </Button>
          <Button
            variant='outlined'
            startIcon={<BugReportIcon />}
            onClick={handleReportBug}>
            Report Issue
          </Button>
        </Stack>

        <Button
          variant='text'
          onClick={() => setShowDetails(!showDetails)}
          sx={{ mb: 2 }}>
          {showDetails ? "Hide" : "Show"} Technical Details
        </Button>

        <Collapse in={showDetails}>
          <Paper
            variant='outlined'
            sx={{
              p: 2,
              mt: 2,
              textAlign: "left",
              bgcolor: "grey.50",
              maxHeight: 300,
              overflow: "auto",
            }}>
            <Typography
              variant='body2'
              component='pre'
              sx={{ fontSize: "0.75rem" }}>
              {JSON.stringify(getErrorDetails(), null, 2)}
            </Typography>
            {error.stack && (
              <>
                <Typography variant='body2' sx={{ mt: 2, fontWeight: "bold" }}>
                  Stack Trace:
                </Typography>
                <Typography
                  variant='body2'
                  component='pre'
                  sx={{ fontSize: "0.75rem" }}>
                  {error.stack}
                </Typography>
              </>
            )}
            {errorInfo?.componentStack && (
              <>
                <Typography variant='body2' sx={{ mt: 2, fontWeight: "bold" }}>
                  Component Stack:
                </Typography>
                <Typography
                  variant='body2'
                  component='pre'
                  sx={{ fontSize: "0.75rem" }}>
                  {errorInfo.componentStack}
                </Typography>
              </>
            )}
          </Paper>
        </Collapse>

        <Typography variant='body2' color='text.secondary' sx={{ mt: 3 }}>
          If this problem persists, please contact Sam on Teams
        </Typography>
      </Paper>
    </Box>
  );
}
